// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { produce } from 'immer';
import { isDefined } from '@shared/utils';
import type {
  AdvisoryInstitution,
  InstitutionAccount,
  InstitutionCard,
  InstitutionLoan,
  InstitutionPortfolio,
} from '@shared/data-access/types/brokerage-types';

/**
 * Converts a numeric value using the provided exchange rate if the value is not
 * null.
 *
 * @param value - The numeric value to convert (can be null or undefined).
 * @param rate - The exchange rate to apply.
 * @returns The converted value or null if the original value was null/undefined.
 */
const convertValue = (
  value: number | null | undefined,
  rate: number,
): number | null => {
  return isDefined(value) ? value * rate : null;
};

/**
 * Gets the exchange rate for a currency, defaulting to 1 if not found.
 *
 * @param currency - The currency code.
 * @param exchangeRates - A record of exchange rates.
 * @returns The exchange rate for the currency or 1 if not found.
 */
const getExchangeRate = (
  currency: string | null | undefined,
  exchangeRates: Record<string, number>,
): number => {
  return currency ? exchangeRates[currency] ?? 1 : 1;
};

/**
 * Converts currency values for an account product, including cash, debt, available
 * liquidity, and investment.
 *
 * @param account - The account to convert.
 * @param exchangeRates - A record of exchange rates.
 */
const convertAccount = (
  account: InstitutionAccount,
  exchangeRates: Record<string, number>,
): void => {
  const rate = getExchangeRate(account.currency, exchangeRates);
  account.cash = convertValue(account.cash, rate);
  account.debt = convertValue(account.debt, rate);
  account.available = convertValue(account.available, rate);
  account.investment = convertValue(account.investment, rate);
};

/**
 * Converts currency values for a card product.
 *
 * @param card - The card to convert.
 * @param exchangeRates - A record of exchange rates.
 */
const convertCard = (
  card: InstitutionCard,
  exchangeRates: Record<string, number>,
): void => {
  const rate = getExchangeRate(card.currency, exchangeRates);
  card.limit *= rate;
  card.disposed = convertValue(card.disposed, rate);
};

/**
 * Converts currency values for a loan product.
 *
 * @param loan - The loan to convert.
 * @param exchangeRates - A record of exchange rates.
 */
const convertLoan = (
  loan: InstitutionLoan,
  exchangeRates: Record<string, number>,
): void => {
  const rate = getExchangeRate(loan.currency, exchangeRates);
  loan.granted_capital = convertValue(loan.granted_capital, rate);
  loan.total_amortized = convertValue(loan.total_amortized, rate);
  loan.total_interest = convertValue(loan.total_interest, rate);
  loan.next_installment = convertValue(loan.next_installment, rate);
  loan.pending_amount = convertValue(loan.pending_amount, rate);
};

/**
 * Converts currency values for a portfolio product.
 *
 * @param portfolio - The portfolio to convert.
 * @param exchangeRates - A record of exchange rates.
 */
const convertPortfolio = (
  portfolio: InstitutionPortfolio,
  exchangeRates: Record<string, number>,
): void => {
  const rate = getExchangeRate(portfolio.currency, exchangeRates);
  portfolio.investment = convertValue(portfolio.investment, rate);
};

/**
 * Converts the currency of institutions' products based on provided exchange rates.
 *
 * @param institutions - An array of institutions with products to be converted.
 * @param exchangeRates - A record of exchange rates where the key is the currency
 *   code and the value is the exchange rate.
 * @returns An array of institutions with converted currency values using immer for
 *   immutable updates.
 */
export const convertInstitutionsCurrency = (
  institutions: AdvisoryInstitution[],
  exchangeRates: Record<string, number>,
): AdvisoryInstitution[] => {
  if (institutions.length === 0) {
    return [];
  }

  return produce(institutions, draft => {
    for (const institution of draft) {
      institution.error =
        institution.error === 'success' ? null : institution.error;
      institution.products.accounts.forEach(account =>
        convertAccount(account, exchangeRates),
      );
      institution.products.cards.forEach(card => convertCard(card, exchangeRates));
      institution.products.loans.forEach(loan => convertLoan(loan, exchangeRates));
      institution.products.portfolios.forEach(portfolio =>
        convertPortfolio(portfolio, exchangeRates),
      );
    }
  });
};
